import React, { Dispatch, SetStateAction } from 'react'
import { capitalize } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Button, DataListItem, Icon, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { useRecurringExportContext } from 'hooks/context/useRecurringExportContext'
import { useDeleteReportScheduleMutation } from 'hooks/mutations/useDeleteReportScheduleMutation'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  ExportTabItems,
  RecurringExportSteps,
  ReportSchedule,
  ReportScheduleFrequency,
} from 'types/transactionsExport'
import { AmplitudeEvent, AmplitudeEventAction } from 'utils/amplitude/amplitudeEvents'
import { buildBaseAmplitudeProperties } from 'utils/export/exportHandlers'
import track from 'utils/tracker'
import { getDayOfWeek } from 'utils/transactionsExports'

export interface ReportScheduleProps {
  reportSchedule: ReportSchedule
  setIsDeleteError: Dispatch<SetStateAction<boolean>>
}

export const ReportScheduleItem = ({ reportSchedule, setIsDeleteError }: ReportScheduleProps) => {
  const { t } = useTranslation()
  const { setCurrentReportSchedule, setIsUpdatingReportSchedule, setStep, handleClose } =
    useRecurringExportContext()

  const { mutate } = useDeleteReportScheduleMutation()

  const amplitudeProperties = buildBaseAmplitudeProperties({
    type: ExportTabItems.RECURRING,
  })

  const deleteReportSchedule = () => {
    setIsDeleteError(false)
    mutate(reportSchedule?.id, {
      onSuccess: () => {
        track.Amp.track(AmplitudeEvent.EOF_DELETE_EXPORT_SUCCESS, amplitudeProperties)
        toast({ header: t('exports.reportScheduleDeleted'), variant: 'info' })
        handleClose()
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_DELETE_EXPORT_ERROR, {
          ...amplitudeProperties,
          error,
        })
        setIsDeleteError(true)
        logSentryError('Error from ReportScheduleItem - deleteReportSchedule', error)
      },
    })
  }

  const handleEditSchedule = () => {
    track.Amp.track(AmplitudeEvent.EDIT_EXPORT_INITIATE, {
      ...amplitudeProperties,
      action: AmplitudeEventAction.CLICK,
    })
    setIsUpdatingReportSchedule(true)
    setCurrentReportSchedule(reportSchedule)
    setStep(RecurringExportSteps.FORM)
  }

  const getScheduleDescription = (reportSchedule: ReportSchedule) => {
    if (!reportSchedule) {
      return ''
    }

    const timeInfo = `${t('exports.at')} ${reportSchedule.time} ${reportSchedule.timezone}`
    const formatInfo = reportSchedule.format?.toUpperCase()

    switch (reportSchedule.frequencyType) {
      case ReportScheduleFrequency.DAILY:
        return `${capitalize(reportSchedule.frequencyType.toLowerCase())} ${timeInfo}, ${formatInfo}`

      case ReportScheduleFrequency.WEEKLY:
        return `${capitalize(reportSchedule.frequencyType.toLowerCase())} ${t('exports.on')} ${getDayOfWeek(reportSchedule.frequencyDay)} ${timeInfo}, ${formatInfo}`

      case ReportScheduleFrequency.MONTHLY:
        return `${t('exports.firstOfMonth')} ${timeInfo}, ${formatInfo}`

      default:
        return ''
    }
  }

  const scheduleDescription = getScheduleDescription(reportSchedule)

  return (
    <DataListItem
      id="report-schedule"
      testId="report-schedule"
      title={t('exports.currentSchedule')}
      body={scheduleDescription}
      layout="vertical"
      ActionButtons={[
        <Button
          key={1}
          testId="edit-schedule-button"
          variant="ghost"
          noPadding
          onClick={handleEditSchedule}
          iconElement={<Icon name="SimplePencilCustom" />}
        />,
        <Button
          key={2}
          testId="delete-schedule-button"
          variant="ghost"
          noPadding
          onClick={deleteReportSchedule}
          iconElement={<Icon name="Trash" />}
        />,
      ]}
    />
  )
}
